import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { business } from '../../services/api';
import { useDevMode } from '../../utils/environment';
import { createServerSupabaseClient, getSessionFromSSR, getCurrentUserFromSSR, hasBusinessOrAdminPermission, normalizeUser } from '@/services/supabase';
import { 
  ShoppingBagIcon, 
  ClipboardDocumentListIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';


type StatCardProps = {
  title: string;
  value: string;
  icon: React.ReactNode;
  change?: {
    value: number;
    isPositive: boolean;
  };
  color: string;
  iconColor: string;
};

const StatCard = ({ title, value, icon, change, color, iconColor }: StatCardProps) => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          
          {change && (
            <div className="mt-2 flex items-center">
              {change.isPositive ? (
                <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm ${change.isPositive ? 'text-green-500' : 'text-red-500'}`}>
                {change.value}% from last month
              </span>
            </div>
          )}
        </div>
        
        <div className={`p-3 ${color} rounded-lg`}>
          <div className={`${iconColor}`}>
            {icon}
          </div>
        </div>
      </div>
    </div>
    
    <div className={`h-1 w-full ${color}`}></div>
  </div>
);

type RecentOrderProps = {
  id: string;
  customer: string;
  date: string;
  amount: string;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
};

const RecentOrder = ({ id, customer, date, amount, status }: RecentOrderProps) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-navy-blue">
        #{id}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {customer}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {date}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        {amount}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[status]}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </td>
    </tr>
  );
};

type ExpiringItemProps = {
  id: string;
  name: string;
  expiryDate: string;
  daysLeft: number;
  quantity: number;
};

const ExpiringItem = ({ id, name, expiryDate, daysLeft, quantity }: ExpiringItemProps) => {
  const getUrgencyColor = (days: number) => {
    if (days <= 1) return 'text-red-600';
    if (days <= 3) return 'text-orange-500';
    return 'text-yellow-500';
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-200">
      <div className="flex items-center">
        <ExclamationTriangleIcon className={`h-5 w-5 mr-2 ${getUrgencyColor(daysLeft)}`} />
        <div>
          <p className="text-sm font-medium text-gray-900">{name}</p>
          <p className="text-xs text-gray-500">Expires: {expiryDate} ({daysLeft} days left)</p>
        </div>
      </div>
      <div className="flex items-center">
        <span className="text-sm text-gray-500 mr-4">Qty: {quantity}</span>
        <Link 
          to={`/products/edit/${id}`}
          className="text-xs font-medium text-sky-blue hover:text-teal"
        >
          Edit
        </Link>
      </div>
    </div>
  );
};

// Server-side function that can be used with Next.js getServerSideProps
export async function getDashboardDataSSR(context: any) {
  try {
    // Create a server-side Supabase client
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the session using the SSR method
    const session = await getSessionFromSSR(supabaseClient);
    
    // If no session, return null
    if (!session) {
      return { user: null, data: null };
    }
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions using the centralized function
    if (!hasBusinessOrAdminPermission(user)) {
      return { user: null, data: null };
    }
    
    // Fetch dashboard data
    const dashboardData = await business.statistics.getDashboardStats(user?.business_id || '');
    
    return {
      user,
      data: dashboardData
    };
  } catch (error) {
    console.error('Error in getDashboardDataSSR:', error);
    return { user: null, data: null };
  }
}

// Props interface for Dashboard component to accept SSR data
interface DashboardProps {
  initialUser?: any;
  initialData?: any;
}

export default function Dashboard({ initialUser, initialData }: DashboardProps = {}) {
  const [isLoading, setIsLoading] = useState(true);
  const isDevEnvironment = useDevMode();
  const [analytics, setAnalytics] = useState<any>({
    totalSales: 0,
    totalOrders: 0,
    totalProducts: 0,
    salesChange: 0,
    ordersChange: 0,
    productsChange: 0
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [expiringItems, setExpiringItems] = useState<any[]>([]);

  useEffect(() => {
    // If we have initialData from SSR, use it directly
    if (initialData && initialUser) {
      setAnalytics({
        totalSales: initialData.totalSales || 0,
        totalOrders: initialData.totalOrders || 0,
        totalProducts: initialData.totalProducts || 0,
        salesChange: initialData.salesChange || 0,
        ordersChange: initialData.ordersChange || 0,
        productsChange: initialData.activeProductsPercentage || 0,
      });
      
      // Set mock data for now (would be replaced with real data from SSR in production)
      setRecentOrders([
        { id: '1001', customer: 'Local Food Bank', date: '2025-05-05', amount: '$125.50', status: 'completed' as const },
        { id: '1002', customer: 'Community Kitchen', date: '2025-05-05', amount: '$78.25', status: 'processing' as const },
        { id: '1003', customer: 'Shelter Outreach', date: '2025-05-04', amount: '$210.00', status: 'pending' as const },
        { id: '1004', customer: 'Youth Center', date: '2025-05-03', amount: '$45.75', status: 'completed' as const },
        { id: '1005', customer: 'Senior Center', date: '2025-05-02', amount: '$92.30', status: 'cancelled' as const },
      ]);
      
      setExpiringItems([
        { id: '101', name: 'Organic Apples (5lb bag)', expiryDate: '2025-05-08', daysLeft: 2, quantity: 12 },
        { id: '102', name: 'Whole Grain Bread', expiryDate: '2025-05-07', daysLeft: 1, quantity: 8 },
        { id: '103', name: 'Fresh Milk (1 gallon)', expiryDate: '2025-05-09', daysLeft: 3, quantity: 15 },
        { id: '104', name: 'Mixed Berries', expiryDate: '2025-05-07', daysLeft: 1, quantity: 6 },
      ]);
      
      setIsLoading(false);
      return;
    }
    
    // If no SSR data, fall back to client-side fetching
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        // Check if we're in dev mode
        const isDevMode = () => {
          const devMode = localStorage.getItem('devMode');
          return devMode ? JSON.parse(devMode).active : false;
        };
        
        // Get current user directly from Supabase using the centralized client
        const { createClient } = await import('../../services/supabase');
        const supabase = createClient();
        const { data: { session } } = await (await supabase).auth.getSession();
        
        // Check if we have a valid session
        if (!session?.user && !isDevMode()) {
          console.error('No valid Supabase session found');
          return;
        }
        
        // If we're not in dev mode, check if user has proper permissions
        if (!isDevMode() && session?.user) {
          // Use normalizeUser to create a consistent user object
          console.debug('Using normalizeUser in Dashboard fetchDashboardData');
          const normalizedUser = normalizeUser(session.user);
          
          // Get role from normalized user
          const userRole = normalizedUser.role || 'business';
          
          // Preserve original implementation for backward compatibility
          const isBusiness = userRole === 'business';
          const isSuperAdmin = userRole === 'super_admin';
          
          if (!isBusiness && !isSuperAdmin) {
            console.error('Current user does not have business or super_admin role');
            return;
          }
        }
        
        try {
          // Get the normalized user if session exists
          const normalizedUser = session?.user ? normalizeUser(session.user) : null;
          
          // Fetch real-time analytics from backend
          const metrics = await business.statistics.getDashboardStats(normalizedUser?.business_id || '');
          console.log('Dashboard metrics received:', metrics); // For debugging
          
          // Map API data to our analytics data format
          const analyticsData = {
            totalSales: metrics?.totalSales || 0,
            totalOrders: metrics?.totalOrders || 0,
            totalProducts: metrics?.totalProducts || 0,
            // Use the actual change percentages from the backend
            salesChange: metrics?.salesChange || 0,
            ordersChange: metrics?.ordersChange || 0,
            productsChange: metrics?.activeProductsPercentage || 0,
          };
          setAnalytics(analyticsData);
        } catch (apiError) {
          console.error('Error fetching analytics data:', apiError);
          // Keep the default values in state
        }
        
        // For now, we'll keep using mock data for orders and products
        // until those APIs are implemented
        
        // Fetch recent orders
        // TODO: Replace with real API call once available
        const ordersData = [
          { id: '1001', customer: 'Local Food Bank', date: '2025-05-05', amount: '$125.50', status: 'completed' as const },
          { id: '1002', customer: 'Community Kitchen', date: '2025-05-05', amount: '$78.25', status: 'processing' as const },
          { id: '1003', customer: 'Shelter Outreach', date: '2025-05-04', amount: '$210.00', status: 'pending' as const },
          { id: '1004', customer: 'Youth Center', date: '2025-05-03', amount: '$45.75', status: 'completed' as const },
          { id: '1005', customer: 'Senior Center', date: '2025-05-02', amount: '$92.30', status: 'cancelled' as const },
        ];
        setRecentOrders(ordersData);
        
        // Fetch expiring products
        // TODO: Replace with real API call once available
        const productsData = [
          { 
            id: '101', 
            name: 'Organic Apples (5lb bag)', 
            expiryDate: '2025-05-08', 
            daysLeft: 2, 
            quantity: 12 
          },
          { 
            id: '102', 
            name: 'Whole Grain Bread', 
            expiryDate: '2025-05-07', 
            daysLeft: 1, 
            quantity: 8 
          },
          { 
            id: '103', 
            name: 'Fresh Milk (1 gallon)', 
            expiryDate: '2025-05-09', 
            daysLeft: 3, 
            quantity: 15 
          },
          { 
            id: '104', 
            name: 'Mixed Berries', 
            expiryDate: '2025-05-07', 
            daysLeft: 1, 
            quantity: 6 
          },
        ];
        setExpiringItems(productsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-blue"></div>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 w-full">
            <StatCard
              title="Total Sales"
              value={`$${analytics.totalSales.toFixed(2)}`}
              icon={<ShoppingBagIcon className="h-6 w-6 text-white" />}
              change={{ value: analytics.salesChange, isPositive: analytics.salesChange > 0 }}
              color="bg-navy-blue"
              iconColor="text-white"
            />
            <StatCard
              title="Total Orders"
              value={analytics.totalOrders.toString()}
              icon={<ClipboardDocumentListIcon className="h-6 w-6 text-white" />}
              change={{ value: analytics.ordersChange, isPositive: analytics.ordersChange > 0 }}
              color="bg-sky-blue"
              iconColor="text-white"
            />
            <StatCard
              title="Active Products"
              value={analytics.totalProducts.toString()}
              icon={<ShoppingBagIcon className="h-6 w-6 text-white" />}
              change={{ value: Math.abs(analytics.productsChange), isPositive: analytics.productsChange > 0 }}
              color="bg-teal"
              iconColor="text-white"
            />
          </div>
          
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-3 w-full">
            <h2 className="text-lg font-medium text-navy-blue mb-2">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
              <Link
                to={isDevEnvironment ? "/dev/business/products/add" : "/business/products/add"}
                className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <ShoppingBagIcon className="h-7 w-7 text-navy-blue mb-1.5" />
                <span className="text-sm font-medium text-gray-900">Add Product</span>
              </Link>
              <Link
                to={isDevEnvironment ? "/dev/business/products/bulk" : "/business/products/bulk"}
                className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <ShoppingBagIcon className="h-7 w-7 text-sky-blue mb-1.5" />
                <span className="text-sm font-medium text-gray-900">Bulk Upload</span>
              </Link>
              <Link
                to={isDevEnvironment ? "/dev/business/orders" : "/business/orders"}
                className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <ClipboardDocumentListIcon className="h-7 w-7 text-teal mb-1.5" />
                <span className="text-sm font-medium text-gray-900">View Orders</span>
              </Link>
              <Link
                to={isDevEnvironment ? "/dev/business/statistics/analytics" : "/statistics/analytics"}
                className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <ClipboardDocumentListIcon className="h-7 w-7 text-purple mb-1.5" />
                <span className="text-sm font-medium text-gray-900">View Analytics</span>
              </Link>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 w-full">
            {/* Recent Orders */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden w-full">
              <div className="px-3 py-2 border-b border-gray-200">
                <h2 className="text-lg font-medium text-navy-blue">Recent Orders</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recentOrders.map((order) => (
                      <RecentOrder
                        key={order.id}
                        id={order.id}
                        customer={order.customer}
                        date={order.date}
                        amount={order.amount}
                        status={order.status}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-gray-200">
                <Link
                  to={isDevEnvironment ? "/dev/business/orders" : "/business/orders"}
                  className="text-sm font-medium text-sky-blue hover:text-teal"
                >
                  View all orders
                </Link>
              </div>
            </div>
            
            {/* Expiring Items */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden w-full">
              <div className="px-3 py-2 border-b border-gray-200">
                <h2 className="text-lg font-medium text-navy-blue">Items Expiring Soon</h2>
              </div>
              <div className="p-3">
                {expiringItems.length > 0 ? (
                  <div className="space-y-1">
                    {expiringItems.map((item) => (
                      <ExpiringItem
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        expiryDate={item.expiryDate}
                        daysLeft={item.daysLeft}
                        quantity={item.quantity}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">No items expiring soon.</p>
                )}
              </div>
              <div className="px-3 py-2 border-t border-gray-200">
                <Link
                  to={isDevEnvironment ? "/dev/business/products" : "/business/products"}
                  className="text-sm font-medium text-sky-blue hover:text-teal"
                >
                  Manage inventory
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
